import React, { useEffect, useMemo, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import LinkIcon from '@mui/icons-material/Link';
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import {
  StyledCrmDialog,
  CrmDialogTitle,
  CrmDialogCloseButton,
  CrmDialogContent,
  CrmDialogActions,
  CrmField,
  CrmPrimaryButton,
  CrmSecondaryButton,
  CrmPanel,
  stageColor,
} from './AdminCrm.styled';

const emptyForm = {
  businessName: '',
  ownerContact: '',
  phone: '',
  email: '',
  city: '',
  howFound: '',
  tradeIds: [],
  subServices: '',
  statusId: '',
  relationshipManagerId: '',
  firstOutreachDate: '',
  lastOutreachDate: '',
  outreachChannelId: '',
  outreachAttempts: '',
  notes: '',
};

function platformBusinessId(business) {
  return business?.BusinessId || business?.id || '';
}

function prospectToForm(prospect) {
  if (!prospect) return { ...emptyForm };
  return {
    businessName: prospect.businessName || '',
    ownerContact: prospect.ownerContact || '',
    phone: prospect.phone || '',
    email: prospect.email || '',
    city: prospect.city || '',
    howFound: prospect.howFound || '',
    tradeIds: Array.isArray(prospect.tradeIds) ? [...prospect.tradeIds] : [],
    subServices: prospect.subServices || '',
    statusId: prospect.statusId || '',
    relationshipManagerId: prospect.relationshipManagerId || '',
    firstOutreachDate: prospect.firstOutreachDate || '',
    lastOutreachDate: prospect.lastOutreachDate || '',
    outreachChannelId: prospect.outreachChannelId || '',
    outreachAttempts:
      prospect.outreachAttempts == null ? '' : String(prospect.outreachAttempts),
    notes: prospect.notes || '',
  };
}

function validateForm(form) {
  const businessName = form.businessName.trim();
  const ownerContact = form.ownerContact.trim();
  const phone = form.phone.trim();
  const email = form.email.trim();
  if (!businessName && !ownerContact) {
    return 'Enter a business name or owner / contact.';
  }
  if (!phone && !email) {
    return 'Enter a phone number or email.';
  }
  if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return 'Enter a valid email address.';
  }
  if (!form.tradeIds?.length) {
    return 'Select at least one trade.';
  }
  if (!form.statusId) {
    return 'Select a pipeline stage.';
  }
  return null;
}

function formToBody(form) {
  const attemptsRaw = String(form.outreachAttempts ?? '').trim();
  let outreachAttempts = null;
  if (attemptsRaw !== '') {
    const n = Number(attemptsRaw);
    outreachAttempts = Number.isFinite(n) ? n : null;
  }
  return {
    businessName: form.businessName.trim(),
    ownerContact: form.ownerContact.trim(),
    phone: form.phone.trim(),
    email: form.email.trim(),
    city: form.city.trim(),
    howFound: form.howFound.trim(),
    tradeIds: form.tradeIds,
    subServices: form.subServices.trim(),
    statusId: form.statusId,
    relationshipManagerId: form.relationshipManagerId || '',
    firstOutreachDate: form.firstOutreachDate.trim(),
    lastOutreachDate: form.lastOutreachDate.trim(),
    outreachChannelId: form.outreachChannelId || '',
    outreachAttempts,
    notes: form.notes.trim(),
  };
}

function AdminCrmProspectModal({
  open,
  prospect,
  settings,
  businesses = [],
  onClose,
  onSave,
  onDelete,
  onOpenLinkBusiness,
  saving = false,
}) {
  const isEdit = Boolean(prospect?.id);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    const next = prospectToForm(prospect);
    if (!isEdit && settings?.pipelineStages?.length) {
      const defaultStage =
        settings.pipelineStages.find((s) => s.id === 'not-contacted' && s.active) ||
        settings.pipelineStages.find((s) => s.active);
      if (defaultStage && !next.statusId) next.statusId = defaultStage.id;
    }
    setForm(next);
    setError(null);
  }, [open, prospect, isEdit, settings]);

  const tradeOptions = useMemo(() => {
    const active = (settings?.trades || []).filter((t) => t.active);
    const selectedInactive = (settings?.trades || []).filter(
      (t) => !t.active && form.tradeIds.includes(t.id),
    );
    return [...active, ...selectedInactive].sort((a, b) => a.sortOrder - b.sortOrder);
  }, [settings, form.tradeIds]);

  const stageOptions = useMemo(() => {
    const stages = settings?.pipelineStages || [];
    return stages
      .filter((s) => s.active || s.id === form.statusId)
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [settings, form.statusId]);

  const channelOptions = useMemo(() => {
    const channels = settings?.outreachChannels || [];
    return channels
      .filter((c) => c.active || c.id === form.outreachChannelId)
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [settings, form.outreachChannelId]);

  const rmOptions = useMemo(() => {
    const rms = settings?.relationshipManagers || [];
    return rms
      .filter((r) => r.active || r.id === form.relationshipManagerId)
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [settings, form.relationshipManagerId]);

  const linkedBusiness = useMemo(() => {
    if (!prospect?.businessId) return null;
    return (
      businesses.find(
        (business) => platformBusinessId(business) === prospect.businessId,
      ) || null
    );
  }, [businesses, prospect?.businessId]);

  const isLinked = Boolean(prospect?.businessId);
  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    const validationError = validateForm(form);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    try {
      await onSave(formToBody(form), {
        previousStatusId: prospect?.statusId || '',
        previousBusinessId: prospect?.businessId || '',
      });
    } catch (e) {
      setError(e.message || 'Failed to save prospect');
    }
  };

  return (
    <StyledCrmDialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="admin-crm-prospect-title"
    >
      <CrmDialogTitle id="admin-crm-prospect-title">
        <span className="crm-dialog-icon-wrap" aria-hidden>
          <PeopleAltOutlinedIcon sx={{ color: '#0d4efe' }} />
        </span>
        {isEdit ? 'Edit prospect' : 'Add prospect'}
        <CrmDialogCloseButton
          aria-label="Close"
          onClick={onClose}
          disabled={saving}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </CrmDialogCloseButton>
      </CrmDialogTitle>

      <CrmDialogContent>
        {error && (
          <Alert severity="error" sx={{ borderRadius: '10px' }}>
            {error}
          </Alert>
        )}

        {isLinked && (
          <Alert
            severity="success"
            sx={{ borderRadius: '10px' }}
            action={
              onOpenLinkBusiness ? (
                <CrmSecondaryButton
                  size="small"
                  variant="outlined"
                  startIcon={<LinkIcon />}
                  onClick={onOpenLinkBusiness}
                  disabled={saving}
                >
                  Change
                </CrmSecondaryButton>
              ) : null
            }
          >
            Linked to{' '}
            {linkedBusiness?.BusinessName || prospect.businessId}. Saving as
            Onboarded keeps this relationship.
          </Alert>
        )}

        <CrmPanel>
          <CrmField
            fullWidth
            size="small"
            label="Business name"
            value={form.businessName}
            onChange={(e) => setField('businessName', e.target.value)}
            helperText="Required if owner / contact is empty"
          />
          <CrmField
            fullWidth
            size="small"
            label="Owner / contact"
            value={form.ownerContact}
            onChange={(e) => setField('ownerContact', e.target.value)}
            sx={{ mt: 2 }}
            helperText="Required if business name is empty"
          />
          <CrmField
            fullWidth
            size="small"
            label="Phone"
            value={form.phone}
            onChange={(e) => setField('phone', e.target.value)}
            sx={{ mt: 2 }}
            helperText="Required if email is empty"
          />
          <CrmField
            fullWidth
            size="small"
            label="Email"
            value={form.email}
            onChange={(e) => setField('email', e.target.value)}
            sx={{ mt: 2 }}
            helperText="Required if phone is empty"
          />
          <CrmField
            fullWidth
            size="small"
            label="City"
            value={form.city}
            onChange={(e) => setField('city', e.target.value)}
            sx={{ mt: 2 }}
          />
          <CrmField
            fullWidth
            size="small"
            label="How was this business found?"
            value={form.howFound}
            onChange={(e) => setField('howFound', e.target.value)}
            sx={{ mt: 2 }}
          />
        </CrmPanel>

        <CrmPanel>
          <Autocomplete
            multiple
            options={tradeOptions}
            getOptionLabel={(o) => o.label}
            value={tradeOptions.filter((t) => form.tradeIds.includes(t.id))}
            onChange={(_, selected) =>
              setField(
                'tradeIds',
                selected.map((s) => s.id),
              )
            }
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.id}
                  label={option.label}
                  size="small"
                />
              ))
            }
            renderInput={(params) => (
              <CrmField {...params} size="small" label="Trades" required />
            )}
          />
          <CrmField
            fullWidth
            size="small"
            label="Sub-services"
            value={form.subServices}
            onChange={(e) => setField('subServices', e.target.value)}
            sx={{ mt: 2 }}
          />
          <FormControl fullWidth size="small" sx={{ mt: 2 }}>
            <InputLabel id="crm-status-label">Pipeline stage</InputLabel>
            <Select
              labelId="crm-status-label"
              label="Pipeline stage"
              value={form.statusId}
              onChange={(e) => setField('statusId', e.target.value)}
              disabled={isLinked}
              sx={{ borderRadius: '10px', background: '#fff' }}
            >
              {stageOptions.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  <Box
                    component="span"
                    aria-hidden
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      backgroundColor: stageColor(s.id),
                      display: 'inline-block',
                      mr: 1,
                      flexShrink: 0,
                    }}
                  />
                  {s.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {isLinked && (
            <Typography variant="caption" sx={{ color: '#5a6478', mt: 0.75, display: 'block' }}>
              Status stays Onboarded while a platform business is linked.
            </Typography>
          )}
          <FormControl fullWidth size="small" sx={{ mt: 2 }}>
            <InputLabel id="crm-rm-label">Relationship manager</InputLabel>
            <Select
              labelId="crm-rm-label"
              label="Relationship manager"
              value={form.relationshipManagerId}
              onChange={(e) => setField('relationshipManagerId', e.target.value)}
              sx={{ borderRadius: '10px', background: '#fff' }}
            >
              <MenuItem value="">
                <em>Unassigned</em>
              </MenuItem>
              {rmOptions.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" sx={{ mt: 2 }}>
            <InputLabel id="crm-channel-label">Outreach channel</InputLabel>
            <Select
              labelId="crm-channel-label"
              label="Outreach channel"
              value={form.outreachChannelId}
              onChange={(e) => setField('outreachChannelId', e.target.value)}
              sx={{ borderRadius: '10px', background: '#fff' }}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {channelOptions.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <CrmField
            fullWidth
            size="small"
            label="First outreach date"
            value={form.firstOutreachDate}
            onChange={(e) => setField('firstOutreachDate', e.target.value)}
            sx={{ mt: 2 }}
            placeholder="YYYY-MM-DD"
          />
          <CrmField
            fullWidth
            size="small"
            label="Last outreach date"
            value={form.lastOutreachDate}
            onChange={(e) => setField('lastOutreachDate', e.target.value)}
            sx={{ mt: 2 }}
            placeholder="YYYY-MM-DD"
          />
          <CrmField
            fullWidth
            size="small"
            label="# Outreach attempts"
            type="number"
            value={form.outreachAttempts}
            onChange={(e) => setField('outreachAttempts', e.target.value)}
            sx={{ mt: 2 }}
            inputProps={{ min: 0 }}
          />
          <CrmField
            fullWidth
            size="small"
            label="Notes"
            value={form.notes}
            onChange={(e) => setField('notes', e.target.value)}
            sx={{ mt: 2 }}
            multiline
            minRows={3}
          />
        </CrmPanel>
      </CrmDialogContent>

      <CrmDialogActions>
        {isEdit && (
          <CrmSecondaryButton
            color="error"
            variant="outlined"
            disabled={saving}
            onClick={onDelete}
            sx={{ mr: 'auto', borderColor: 'rgba(211,47,47,0.4)', color: '#d32f2f' }}
          >
            Delete
          </CrmSecondaryButton>
        )}
        <CrmSecondaryButton variant="outlined" onClick={onClose} disabled={saving}>
          Cancel
        </CrmSecondaryButton>
        <CrmPrimaryButton onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </CrmPrimaryButton>
      </CrmDialogActions>
    </StyledCrmDialog>
  );
}

export default AdminCrmProspectModal;
